import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Chip, Paper, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import SearchableSelect from '../../common/SearchableSelect';
import { FulfilmentService } from '../../../services/fulfilment.service';
import {
  SHIPMENT_STATUSES, SHIPMENT_STATUS_COLORS, ShipmentPage, ShipmentStatus,
} from '../../../interfaces/sales/fulfilment.interface';
import { TrackingLink } from './ChallanFulfilmentTab';
import { AuthService } from '../../../services/auth.service';
import { useToast } from '../../../context/ToastContext';
import { humanizeEnum } from '../../../utils/format';
import { formatDate } from '../../../utils/dateUtils';

const STATUS_OPTIONS = SHIPMENT_STATUSES.map(s => ({ id: s, name: humanizeEnum(s) }));

/** F5: all shipments of the institution, newest first. */
const ShipmentList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution();
  const [status, setStatus] = useState<ShipmentStatus | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(50);
  const [data, setData] = useState<ShipmentPage | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await FulfilmentService.list(institutionId, {
        status: status || undefined, from: from || undefined, to: to || undefined, page, size,
      }));
    } catch (e) { toast.showApiError(e, 'Failed to load shipments'); }
  }, [institutionId, status, from, to, page, size]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.shipments.page">
      <Typography variant="h5" sx={{ mb: 2 }}>Shipments</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }} data-help-section="sales.shipments.filters">
        <SearchableSelect data-help="sales.shipments.status" label="Status" size="small" sx={{ flex: '1 1 220px', maxWidth: 320 }} clearable
          options={STATUS_OPTIONS} getOptionLabel={o => o.name} value={status} onChange={(v: string) => { setStatus((v || '') as ShipmentStatus | ''); setPage(0); }} />
        <TextField data-help="sales.shipments.from" label="Shipped from" type="date" size="small" value={from} InputLabelProps={{ shrink: true }}
          onChange={e => { setFrom(e.target.value); setPage(0); }} sx={{ flex: '1 1 180px', maxWidth: 240 }} />
        <TextField data-help="sales.shipments.to" label="Shipped to" type="date" size="small" value={to} InputLabelProps={{ shrink: true }}
          onChange={e => { setTo(e.target.value); setPage(0); }} sx={{ flex: '1 1 180px', maxWidth: 240 }} />
      </Box>
      <Paper sx={{ overflowX: 'auto' }} data-help-section="sales.shipments.table">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell data-help="sales.shipments.col.shipment">Shipment</TableCell><TableCell data-help="sales.shipments.col.challan">Challan</TableCell><TableCell data-help="sales.shipments.col.order">Order</TableCell><TableCell data-help="sales.shipments.col.customer">Customer</TableCell>
              <TableCell data-help="sales.shipments.col.carrier">Carrier</TableCell><TableCell data-help="sales.shipments.col.tracking">Tracking</TableCell><TableCell align="right" data-help="sales.shipments.col.packages">Packages</TableCell>
              <TableCell data-help="sales.shipments.col.shipped">Shipped</TableCell><TableCell data-help="sales.shipments.col.delivered">Delivered</TableCell><TableCell data-help="sales.shipments.col.status">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.content.map(r => (
              <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/sales/challans/${r.challanId}?tab=fulfilment`)}>
                <TableCell>{r.shipmentNo}</TableCell>
                <TableCell>{r.challanNo}</TableCell>
                <TableCell>{r.orderNo}</TableCell>
                <TableCell>{r.customerName}</TableCell>
                <TableCell>{r.carrier ?? '-'}</TableCell>
                <TableCell onClick={e => e.stopPropagation()}>
                  {r.trackingNo ?? ''} <TrackingLink url={r.trackingUrl} />
                </TableCell>
                <TableCell align="right">{r.packageCount}</TableCell>
                <TableCell>{r.shippedOn ? formatDate(r.shippedOn) : '-'}</TableCell>
                <TableCell>{r.deliveredOn ? formatDate(r.deliveredOn) : '-'}</TableCell>
                <TableCell><Chip size="small" label={humanizeEnum(r.status)} color={SHIPMENT_STATUS_COLORS[r.status]} /></TableCell>
              </TableRow>
            ))}
            {data && data.content.length === 0 && (
              <TableRow><TableCell colSpan={10}><Typography color="text.secondary">No shipments.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination component="div" count={data?.totalElements ?? 0} page={page} rowsPerPage={size}
          rowsPerPageOptions={[25, 50, 100, 200]} onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => { setSize(Number(e.target.value)); setPage(0); }} />
      </Paper>
    </Box>
  );
};

export default ShipmentList;
